import {clamp, isFinite, round} from 'lodash';

/**
 * Bid and ask for the expanded watchlist, which the backend does not provide, and the heatmap color
 * scale for its return columns. The bid and ask are seeded off the ticker, so a ticker shows the same
 * spread across reloads.
 */

export interface SimulatedBidAsk {
    bid: number;
    ask: number;
}

/** A bid/ask spread of a few basis points around the current price. */
export function simulateBidAsk(ticker: string, price: number): SimulatedBidAsk {
    if (!isFinite(price)) return {bid: null, ask: null};

    const rng = mulberry32(hashString(`${ticker}:quote`)),
        spreadBps = 1 + rng() * 8,
        halfSpread = Math.max((price * spreadBps) / 20000, 0.005),
        bid = round(price - halfSpread, 2);

    return {bid, ask: Math.max(round(price + halfSpread, 2), bid + 0.01)};
}

const HEATMAP_GREEN: [number, number, number] = [46, 160, 67];
const HEATMAP_RED: [number, number, number] = [220, 38, 38];
const HEATMAP_MIN_ALPHA = 0.1;
const HEATMAP_MAX_ALPHA = 0.6;

/**
 * Build a heatmap color scale from a set of return values. Returns a function that maps a value
 * to a CSS background color, anchored at 0% (white/transparent) and scaling out to full color at
 * the min (red) or max (green) value observed in the input set. The scale is logarithmic in the size
 * of the return, so the spread between small returns stays visible next to a very large one. Using rgba (rather than mixing
 * toward opaque white) keeps the tint theme-aware, so it reads correctly in both light and dark
 * mode instead of washing out dark cell backgrounds.
 */
export function buildHeatmapScale(values: number[]): (v: number) => string {
    const finite = values.filter(isFinite),
        max = finite.length ? Math.max(...finite, 0) : 0,
        min = finite.length ? Math.min(...finite, 0) : 0;

    return (v: number) => {
        if (!isFinite(v) || v === 0) return null;
        // Log scale, so one outsized return does not leave every other cell near the palest shade
        const intensity = clamp(v > 0 ? Math.log1p(v) / Math.log1p(max || v) : Math.log1p(-v) / Math.log1p(-(min || v)), 0, 1),
            alpha = HEATMAP_MIN_ALPHA + intensity * (HEATMAP_MAX_ALPHA - HEATMAP_MIN_ALPHA),
            [r, g, b] = v > 0 ? HEATMAP_GREEN : HEATMAP_RED;
        return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
    };
}

//------------------------
// Implementation
//------------------------
function hashString(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) {
        h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    }
    return h;
}

// Mulberry32 - small, fast, deterministic PRNG seeded from the ticker hash.
function mulberry32(seed: number): () => number {
    let a = seed;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
